import { useAuth } from "../../../auth/AuthContext";
import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { PanelLoading } from "../../../components/Loading";
import { LotQualityBadge } from "../../../components/LotQualityBadge";
import { useToast } from "../../../components/Toast";
import { formatDate, formatDateTime, formatNumber } from "../../../lib/format";
import { Link } from "react-router-dom";
import Ic from "../../../components/icons.jsx";
import type { ApiError } from "../../../api/client";
import {
  dispatchOrderQualityApi,
  type DispatchOrder,
  type DispatchOrderLine,
  type DispatchOrderStatus,
  type LotEvidence,
  type LotOptions,
} from "../../../api/dispatchOrders";
import { lotDocumentApi } from "../../../api/lotDocuments";
import { traceabilityApi, type LotHit } from "../../../api/traceability";

/**
 * Puštanje LOT-a za nalog za otpremu — poslednja kapija lanca.
 *
 * Prodaja otvara nalog, a kvalitet određuje koji LOT sme da se veže za njega. Sve pre ovoga je merenje;
 * ovde se odlučuje šta izlazi iz fabrike, pa je puštanje svoja radnja sa svojim tragom, a ne polje koje
 * se usput popuni.
 *
 * Ekran namerno NE pokazuje 108.751 nalog prenet iz ERP-a: oni su već otpremljeni i nose RELEASED, pa
 * bi zatrpali onih nekoliko koji danas čekaju odluku.
 */
export default function QualityDispatchReleasePage() {
  const { t } = useTranslation();

  const [orders, setOrders] = useState<DispatchOrder[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await dispatchOrderQualityApi.awaiting();
      setOrders(rows);
      if (!openId && rows.length > 0) setOpenId(rows[0].id);
    } catch (e) {
      setError((e as ApiError).message);
    }
  }, [openId]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function replace(updated: DispatchOrder) {
    setOrders((prev) => (prev ?? []).map((o) => (o.id === updated.id ? updated : o)));
  }

  if (error) return <div className="empty">{error}</div>;
  if (!orders) return <PanelLoading text={t("common.loading", "Učitavanje…")} />;

  const open = orders.find((o) => o.id === openId) ?? null;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("dispatchRelease.title", "Puštanje LOT-a za otpremu")}</h1>
          <div className="sub">
            {t(
              "dispatchRelease.lead",
              "Prodaja otvara nalog, kvalitet određuje koji LOT sme da se veže za njega.",
            )}
          </div>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="card">
          <div className="empty">
            {t("dispatchRelease.none", "Nijedan nalog ne čeka odluku kvaliteta.")}
          </div>
        </div>
      ) : (
        <div className="catalog-layout">
          <aside className="cat-side">
            <div className="group">
              <h4>{t("dispatchRelease.awaitingList", "Čekaju odluku")}</h4>
              <div className="cat-list">
                {orders.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    className={o.id === openId ? "active" : ""}
                    onClick={() => setOpenId(o.id)}
                    title={`${o.companyName ?? ""} · ${formatDate(o.issueDate)}`}
                  >
                    <span
                      style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {o.number}
                      {o.status === "ON_HOLD" && ` · ${t("dispatchRelease.status.ON_HOLD", "zadržan")}`}
                    </span>
                    {o.awaiting > 0 && <span className="n">{o.awaiting}</span>}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <section style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
            {!open ? (
              <div className="card">
                <div className="empty">{t("dispatchRelease.pickOne", "Izaberite nalog sa spiska.")}</div>
              </div>
            ) : (
              <OrderPanel order={open} onChanged={replace} />
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status, awaiting }: { status: DispatchOrderStatus; awaiting: number }) {
  const { t } = useTranslation();
  const tekst =
    status === "ON_HOLD"
      ? t("dispatchRelease.status.ON_HOLD", "zadržan")
      : status === "RELEASED"
        ? t("dispatchRelease.status.RELEASED", "pušten")
        : status === "DRAFT"
          ? t("dispatchRelease.status.DRAFT", "u pripremi")
          : status === "CANCELLED"
            ? t("dispatchRelease.status.CANCELLED", "otkazan")
            : t("dispatchRelease.status.AWAITING_QUALITY", "čeka kvalitet");
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      <span className="tag" style={status === "ON_HOLD" ? znakUpozorenje : undefined}>
        {tekst}
      </span>
      {awaiting > 0 && (
        <span className="tag">{t("dispatchRelease.awaitingN", "{{n}} bez odluke", { n: awaiting })}</span>
      )}
    </div>
  );
}

function OrderPanel({
  order,
  onChanged,
}: {
  order: DispatchOrder;
  onChanged: (o: DispatchOrder) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="card">
        <header className="card-h">
          <div>
            <h3>{order.number}</h3>
            <div className="sub">
              {order.companyName ?? "—"}
              {order.dispositionDate
                ? ` · ${t("dispatchRelease.disposition", "dispozicija {{datum}}", { datum: formatDate(order.dispositionDate) })}`
                : ""}
              {order.vehicle ? ` · ${order.vehicle}` : ""}
              {order.driver ? ` · ${order.driver}` : ""}
            </div>
          </div>
          <StatusBadge status={order.status} awaiting={order.awaiting} />
        </header>
        {order.status === "RELEASED" && order.qualityReleasedAt && (
          <div className="card-b" style={{ paddingTop: 12, paddingBottom: 12 }}>
            <span className="tag" style={znakOk}>
              {t("dispatchRelease.releasedWhole", "Ceo nalog je pušten {{datum}}. Utovar može.", {
                datum: formatDate(order.qualityReleasedAt),
              })}
            </span>
          </div>
        )}
        {order.status === "ON_HOLD" && (
          <div className="card-b" style={{ paddingTop: 12, paddingBottom: 12 }}>
            <span className="tag" style={znakUpozorenje}>
              {t(
                "dispatchRelease.heldWhole",
                "Jedna stavka je zadržana, pa ceo nalog stoji — kamion ne izlazi polupun.",
              )}
            </span>
          </div>
        )}
      </div>
      {order.lines.length === 0 ? (
        <div className="card">
          <div className="empty">{t("dispatchRelease.noLines", "Nalog nema stavki.")}</div>
        </div>
      ) : (
        order.lines.map((line) => <LineCard key={line.id} order={order} line={line} onChanged={onChanged} />)
      )}
    </>
  );
}

function LineCard({
  order,
  line,
  onChanged,
}: {
  order: DispatchOrder;
  line: DispatchOrderLine;
  onChanged: (o: DispatchOrder) => void;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canAct = hasPermission("dispatch_orders:quality_release");
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [holding, setHolding] = useState(false);
  const [reason, setReason] = useState("");
  const [picking, setPicking] = useState(false);

  async function act(fn: () => Promise<DispatchOrder>) {
    setBusy(true);
    try {
      onChanged(await fn());
      setHolding(false);
      setReason("");
      setPicking(false);
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={line.held ? ivicaUpozorenje : line.released ? ivicaOk : undefined}>
      <div className="card-b">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <strong>
            {line.position}. {line.productName ?? line.erpProductId ?? "—"}
          </strong>
          <div style={tiho}>
            {line.quantity != null ? `${line.quantity} ${line.unit ?? ""}` : "—"}
            {line.pallets != null
              ? ` · ${t("dispatchRelease.pallets", "{{n}} paleta", { n: line.pallets })}`
              : ""}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          {line.released && (
            <div style={tiho}>
              {t("dispatchRelease.releasedBy", "Pustio {{ko}}, {{datum}}", {
                ko: line.releasedBy ?? "—",
                datum: formatDate(line.releasedAt ?? ""),
              })}
            </div>
          )}
          {line.held && (
            <div style={tekstUpozorenje}>
              {t("dispatchRelease.heldBy", "Zadržao {{ko}}, {{datum}}", {
                ko: line.heldBy ?? "—",
                datum: formatDate(line.heldAt ?? ""),
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        {line.goodsLotId ? (
          <div>
            <span style={{ fontFamily: "var(--mono)" }}>{line.lotDesignation}</span>{" "}
            <LotQualityBadge status={line.lotQualityStatus} note={line.lotQualityNote} />
            <span style={tiho}>
              {line.lotProductName ? ` · ${line.lotProductName}` : ""}
              {line.lotProducedOn
                ? ` · ${t("dispatchRelease.producedOn", "proizveden {{datum}}", { datum: formatDate(line.lotProducedOn) })}`
                : ""}
              {line.lotExpiresOn
                ? ` · ${t("dispatchRelease.expiresOn", "traje do {{datum}}", { datum: formatDate(line.lotExpiresOn) })}`
                : ""}
            </span>
            {line.lotQualityNote && line.lotQualityStatus && line.lotQualityStatus !== "CONFORMING" && (
              <div style={{ ...tekstUpozorenje, marginTop: 4 }}>{line.lotQualityNote}</div>
            )}
            {line.articleMismatch && (
              <div style={{ ...tekstUpozorenje, marginTop: 4 }}>
                {t(
                  "dispatchRelease.articleMismatch",
                  "LOT-ov artikal se ne poklapa sa artiklom stavke. Proverite pre puštanja.",
                )}
              </div>
            )}
            <LotEvidenceBlock goodsLotId={line.goodsLotId} lot={line.lotDesignation} />
          </div>
        ) : (
          <div style={tiho}>{t("dispatchRelease.noLot", "LOT nije određen.")}</div>
        )}
      </div>

      {line.held && line.holdReason && (
        <p style={{ ...tekstUpozorenje, marginTop: 8 }}>
          {t("dispatchRelease.holdReasonIs", "Razlog: {{razlog}}", { razlog: line.holdReason })}
        </p>
      )}

      {canAct && picking && (
        <LotPicker
          orderId={order.id}
          lineId={line.id}
          busy={busy}
          onCancel={() => setPicking(false)}
          onPick={(lotId, pallets) =>
            void act(() => dispatchOrderQualityApi.assignLot(order.id, line.id, { goodsLotId: lotId, pallets }))
          }
        />
      )}

      {canAct && holding && (
        <div style={{ marginTop: 10 }}>
          <input
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("dispatchRelease.holdPlaceholder", "Zašto LOT ne sme da izađe — prodaja ovo čita")}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button className="btn" type="button" onClick={() => setHolding(false)} disabled={busy}>
              {t("common.cancel", "Odustani")}
            </button>
            <button
              className="btn primary"
              type="button"
              disabled={busy || !reason.trim()}
              onClick={() => void act(() => dispatchOrderQualityApi.hold(order.id, line.id, reason.trim()))}
            >
              {t("dispatchRelease.confirmHold", "Zadrži")}
            </button>
          </div>
        </div>
      )}

      {canAct && !holding && !picking && (
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <button className="btn sm" type="button" onClick={() => setPicking(true)} disabled={busy}>
            <Ic.search /> {line.goodsLotId
              ? t("dispatchRelease.changeLot", "Promeni LOT")
              : t("dispatchRelease.pickLot", "Odredi LOT")}
          </button>
          {line.goodsLotId && !line.released && (
            <button
              className="btn sm primary"
              type="button"
              disabled={busy}
              onClick={() => void act(() => dispatchOrderQualityApi.release(order.id, line.id))}
            >
              {t("dispatchRelease.release", "Pusti za utovar")}
            </button>
          )}
          {line.goodsLotId && !line.held && (
            <button className="btn sm" type="button" disabled={busy} onClick={() => setHolding(true)}>
              {t("dispatchRelease.hold", "Zadrži")}
            </button>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

/**
 * Nalazi i dokumenti LOT-a na stavci, pre odluke o puštanju: nalazi uneti u portal (koliko je van granice i koliko čeka
 * potpis), arhiv laboratorije za LOT, a kad ga nema, za artikal, i dokumenti uz LOT sa stanjem. Ono što traži pažnju
 * piše se bojom upozorenja; „ocenjeno“ u arhivu broji samo rezultate sa vrednošću i granicom koja važi, kao stranica
 * analize, pa LOT bez ocenjenih rezultata nikad ne izgleda kao da je prošao.
 */
function LotEvidenceBlock({ goodsLotId, lot }: { goodsLotId: string; lot: string | null }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const { hasPermission } = useAuth();
  const toast = useToast();
  const [dokazi, setDokazi] = useState<LotEvidence | null>(null);
  const [greska, setGreska] = useState(false);

  useEffect(() => {
    let vazi = true;
    setDokazi(null);
    setGreska(false);
    dispatchOrderQualityApi
      .evidence(goodsLotId)
      .then((d) => {
        if (vazi) setDokazi(d);
      })
      .catch(() => {
        if (vazi) setGreska(true);
      });
    return () => {
      vazi = false;
    };
  }, [goodsLotId]);

  async function preuzmi(id: string) {
    try {
      const { url } = await lotDocumentApi.downloadUrl(id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  if (greska) {
    return <div style={{ ...tiho, marginTop: 8 }}>{t("dispatchRelease.evidence.failed", "Nalazi i dokumenti nisu mogli da se učitaju.")}</div>;
  }
  if (!dokazi) {
    return <div style={{ ...tiho, marginTop: 8 }}>{t("dispatchRelease.evidence.loading", "Čitam nalaze i dokumente…")}</div>;
  }
  const { readings: r, archive: a, documents } = dokazi;
  // Datum ostaje u jednom redu („19. 9. / 2026.“ se čita kao dva podatka), a broj sa razdvajanjem hiljada.
  const datum = (iso: string | null) => formatDate(iso ?? "", lang).replace(/ /g, "\u00a0");
  const broj = (n: number) => formatNumber(n, lang);

  return (
    <div style={dokaziOkvir} role="group" aria-label={t("dispatchRelease.evidence.title", "Nalazi i dokumenti")}>
      <div style={dokaziNaslov}>{t("dispatchRelease.evidence.title", "Nalazi i dokumenti")}</div>
      <div style={r.outOfRange > 0 || r.awaitingSignature > 0 ? tekstUpozorenje : tiho}>
        {r.count === 0
          ? t("dispatchRelease.evidence.readingsNone", "Nalazi u portalu: nema")
          : t(
              "dispatchRelease.evidence.readings",
              "Nalazi u portalu: {{n}}, van granice {{out}}, čeka potpis {{pending}} · poslednji {{at}}",
              { n: broj(r.count), out: broj(r.outOfRange), pending: broj(r.awaitingSignature), at: datum(r.lastAt) },
            )}
        {r.count > 0 && lot && hasPermission("section:quality_readings:read") && (
          <>
            {" · "}
            <Link to={`/quality/readings?q=${encodeURIComponent(lot)}&lot=${goodsLotId}`}>
              {t("dispatchRelease.evidence.open", "otvori")}
            </Link>
          </>
        )}
      </div>
      <div style={a.outside > 0 ? tekstUpozorenje : tiho}>
        {a.analyses > 0
          ? t(
              "dispatchRelease.evidence.archive",
              "Arhiv laboratorije: analiza {{n}}, poslednja {{at}} · ocenjenih rezultata {{judged}}, van granice {{outside}}",
              { n: broj(a.analyses), at: datum(a.lastOn), judged: broj(a.judged), outside: broj(a.outside) },
            )
          : a.articleAnalyses > 0
            ? t(
                "dispatchRelease.evidence.archiveArticle",
                "Arhiv laboratorije: za ovaj LOT nema analize; analiza artikla: {{n}}, poslednja {{at}}",
                { n: broj(a.articleAnalyses), at: datum(a.articleLastOn) },
              )
            : t("dispatchRelease.evidence.archiveNone", "Arhiv laboratorije: nema analize ni za LOT ni za artikal")}
      </div>
      <div style={tiho}>
        {t("dispatchRelease.evidence.documents", "Dokumenti:")}{" "}
        {documents.length === 0
          ? t("dispatchRelease.evidence.documentsNone", "nema")
          : documents.map((d, i) => (
              <span key={d.id}>
                {i > 0 && " · "}
                {t(`lotDoc.kind.${d.kind}`, d.kind)}
                {d.issueNumber ? ` ${d.issueNumber}` : ""} ({t(`lotDoc.state.${d.state}`, d.state)}
                {d.expired ? `, ${t("dispatchRelease.evidence.expired", "istekao")}` : ""})
                {hasPermission("lot_documents:view") && (
                  <>
                    {" "}
                    <button type="button" className="btn ghost sm" style={dugmeUTekstu} onClick={() => void preuzmi(d.id)}>
                      {t("dispatchRelease.evidence.download", "preuzmi")}
                    </button>
                  </>
                )}
              </span>
            ))}
        {hasPermission("section:lot_documents:read") && (
          <>
            {" · "}
            <Link to="/quality/lot-documents">{t("dispatchRelease.evidence.register", "Dokumenti uz LOT")}</Link>
          </>
        )}
      </div>
    </div>
  );
}

/** Traži LOT po oznaci. Pretraga vraća listu i uz oznaku pokazuje artikal, jer se oznaka ponavlja. */
/**
 * Izbor LOT-a za stavku: sa stanja magacina, prvo LOT koji pre ističe. Nude se usaglašeni, a SS samo kad nalog ide
 * kupcu u Srbiji, sa onim što ovaj nalog sme da uzme (stanje manje ono što drže druge porudžbine). Šta nije ponuđeno
 * kaže se brojem i razlogom, da prazan spisak ne izgleda kao da robe nema; neocenjeni LOT-ovi vode u Magacin. Ručna
 * pretraga po oznaci ostaje kao rezerva, za LOT koji stanje iz ERP-a još ne vidi.
 */
function LotPicker({
  orderId,
  lineId,
  busy,
  onPick,
  onCancel,
}: {
  orderId: string;
  lineId: string;
  busy: boolean;
  onPick: (lotId: string, pallets: number | null) => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const [ponuda, setPonuda] = useState<LotOptions | null>(null);
  const [stanjePuklo, setStanjePuklo] = useState(false);
  const [rucno, setRucno] = useState(false);
  const [term, setTerm] = useState("");
  const [hits, setHits] = useState<LotHit[] | null>(null);
  // Odsečen spisak mora da kaže da je odsečen: tih spisak od pedeset redova čitalac čita kao
  // „toliko ih ima" i dalje traži po onome što je već odsečeno (pravilo 15).
  const [odseceno, setOdseceno] = useState(0);
  const [pallets, setPallets] = useState("");
  const [searching, setSearching] = useState(false);
  const palete = () => (pallets.trim() ? Number(pallets) : null);

  useEffect(() => {
    let vazi = true;
    dispatchOrderQualityApi
      .lotOptions(orderId, lineId)
      .then((p) => {
        if (vazi) setPonuda(p);
      })
      .catch(() => {
        if (vazi) setStanjePuklo(true);
      });
    return () => {
      vazi = false;
    };
  }, [orderId, lineId]);

  async function search() {
    if (term.trim().length < 2) return;
    setSearching(true);
    try {
      const nadjeno = await traceabilityApi.search(term.trim());
      setHits(nadjeno.rows);
      setOdseceno(nadjeno.cut ? nadjeno.totalAtMost : 0);
    } finally {
      setSearching(false);
    }
  }

  const kolicina = (q: number, unit: string | null) =>
    `${formatNumber(q, lang, { maximumFractionDigits: 3 })} ${unit ?? ""}`.trim();
  const izostavljeno = ponuda
    ? [
        ponuda.expired > 0 ? t("dispatchRelease.leftOut.expired", "isteklih {{n}}", { n: ponuda.expired }) : null,
        ponuda.unassessed > 0 ? t("dispatchRelease.leftOut.unassessed", "neocenjenih {{n}}", { n: ponuda.unassessed }) : null,
        ponuda.nonconforming > 0
          ? t("dispatchRelease.leftOut.nonconforming", "neusaglašenih {{n}}", { n: ponuda.nonconforming })
          : null,
        ponuda.domesticOnlyAbroad > 0
          ? t("dispatchRelease.leftOut.abroad", "SS, a nalog ide u inostranstvo {{n}}", { n: ponuda.domesticOnlyAbroad })
          : null,
        ponuda.heldByOthers > 0
          ? t("dispatchRelease.leftOut.held", "zauzetih drugim porudžbinama {{n}}", { n: ponuda.heldByOthers })
          : null,
      ].filter(Boolean)
    : [];

  return (
    <div style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 10 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <strong style={{ fontSize: 13 }}>{t("dispatchRelease.fromStock", "Sa stanja magacina, prvo LOT koji pre ističe")}</strong>
        {ponuda?.stockReadAt && (
          <span style={tiho}>
            {t("dispatchRelease.stockReadAt", "stanje iz ERP-a {{at}}", { at: formatDateTime(ponuda.stockReadAt, lang) })}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <input
          className="input"
          style={{ maxWidth: 110 }}
          value={pallets}
          onChange={(e) => setPallets(e.target.value)}
          placeholder={t("dispatchRelease.palletsShort", "paleta")}
          aria-label={t("dispatchRelease.palletsShort", "paleta")}
        />
        <button className="btn sm" type="button" onClick={onCancel} disabled={busy}>
          {t("common.cancel", "Odustani")}
        </button>
      </div>

      {stanjePuklo ? (
        <p style={{ ...tekstUpozorenje, marginTop: 8 }}>
          {t("dispatchRelease.stockFailed", "Stanje nije moglo da se učita; potražite LOT po oznaci.")}
        </p>
      ) : ponuda === null ? (
        <p style={{ ...tiho, marginTop: 8 }}>{t("dispatchRelease.loadingStock", "Čitam stanje…")}</p>
      ) : ponuda.options.length === 0 ? (
        <p style={{ ...tiho, marginTop: 8 }}>
          {t("dispatchRelease.noneOffered", "Nema usaglašenog LOT-a ovog artikla sa slobodnom količinom.")}
        </p>
      ) : (
        <table className="tbl" style={{ marginTop: 8 }}>
          <tbody>
            {ponuda.options.map((o) => (
              <tr key={o.goodsLotId}>
                <td style={{ fontFamily: "var(--mono)" }}>{o.lot ?? "—"}</td>
                <td style={tiho}>{o.fields ?? "—"}</td>
                <td style={{ whiteSpace: "nowrap" }}>{o.expiresOn ? formatDate(o.expiresOn) : "—"}</td>
                <td>
                  <LotQualityBadge status={o.qualityStatus} note={o.qualityNote} />
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {t("dispatchRelease.available", "slobodno {{qty}}", { qty: kolicina(o.available, o.unit) })}
                  {o.heldForOrder > 0 && (
                    <div style={tiho}>
                      {t("dispatchRelease.heldForOrder", "od toga za ovu porudžbinu {{qty}}", {
                        qty: kolicina(o.heldForOrder, o.unit),
                      })}
                    </div>
                  )}
                </td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn sm" type="button" disabled={busy} onClick={() => onPick(o.goodsLotId, palete())}>
                    {t("dispatchRelease.pick", "Izaberi")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {izostavljeno.length > 0 && (
        <p style={{ ...tiho, marginTop: 8 }}>
          {t("dispatchRelease.leftOut", "Nije ponuđeno:")} {izostavljeno.join(", ")}
          {ponuda && ponuda.unassessed > 0 && ponuda.erpProductId && (
            <>
              {" · "}
              <Link to={`/warehouse?q=${encodeURIComponent(ponuda.erpProductId)}&status=OPEN`}>
                {t("dispatchRelease.assessInWarehouse", "oceni u Magacinu")}
              </Link>
            </>
          )}
        </p>
      )}

      {!rucno ? (
        <button type="button" className="btn ghost sm" style={{ marginTop: 6 }} onClick={() => setRucno(true)}>
          {t("dispatchRelease.manualSearch", "LOT nije na spisku? Traži po oznaci")}
        </button>
      ) : (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              className="input"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void search();
              }}
              placeholder={t("dispatchRelease.lotSearch", "Oznaka LOT-a, npr. 150926E4A18542")}
            />
            <button className="btn" type="button" onClick={() => void search()} disabled={searching}>
              {t("common.search", "Traži")}
            </button>
          </div>
          {hits && hits.length === 0 && (
            <p style={{ ...tiho, marginTop: 8 }}>{t("dispatchRelease.noHits", "Nema LOT-a sa tom oznakom.")}</p>
          )}
          {hits && hits.length > 0 && (
            <>
              {odseceno > 0 && (
                <p style={{ ...tiho, marginTop: 8 }}>
                  {t("lotSearch.cut", "Prikazano prvih {{n}} od {{svih}} šarži — suzite pretragu.", {
                    n: hits.length,
                    svih: odseceno,
                  })}
                </p>
              )}
              <table className="tbl" style={{ marginTop: 8 }}>
                <tbody>
                  {hits.map((h) => (
                    <tr key={h.id}>
                      {/* Oznaka i barkod: na soji iz ERP-a oznaka nosi klasu, a traži se po barkodu. */}
                      <td style={{ fontFamily: "var(--mono)" }}>{h.barcode ?? h.designation ?? "—"}</td>
                      <td style={tiho}>{h.productName ?? "—"}</td>
                      <td style={tiho}>{h.producedOn ? formatDate(h.producedOn) : "—"}</td>
                      <td>{h.qualityStatus && <LotQualityBadge status={h.qualityStatus} />}</td>
                      <td style={{ textAlign: "right" }}>
                        <button className="btn sm" type="button" disabled={busy} onClick={() => onPick(h.id, palete())}>
                          {t("dispatchRelease.useThis", "Uzmi ovaj")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ stil */

/*
 * Ekran stoji na sistemu: `catalog-layout`, `cat-side`, `group`, `cat-list`, `card`, `card-h`,
 * `card-b`, `tbl`, `tag`, `empty`. Ovde je samo ono čega u sistemu nema — boja ishoda na znački i
 * na ivici kartice, jer je odluka kvaliteta ono što na ovom ekranu treba da se vidi iz prikrajka.
 */

const tiho: CSSProperties = { color: "var(--ink-3)", fontSize: 12.5 };
const dokaziOkvir: CSSProperties = {
  marginTop: 10,
  padding: "8px 10px",
  background: "var(--surface-2)",
  borderRadius: "var(--r-sm)",
  display: "flex",
  flexDirection: "column",
  gap: 3,
};
const dokaziNaslov: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: "var(--ink-3)",
};
const dugmeUTekstu: CSSProperties = { height: "auto", padding: "0 4px", fontSize: 12.5 };
const tekstUpozorenje: CSSProperties = { color: "var(--warn)", fontSize: 12.5 };

const znakUpozorenje: CSSProperties = {
  borderColor: "var(--warn)",
  color: "var(--warn)",
  background: "transparent",
};
const znakOk: CSSProperties = {
  borderColor: "var(--ok)",
  color: "var(--ok)",
  background: "transparent",
};

/** Puštena stavka je zelena po ivici, zadržana narandžasta — stanje se vidi pre nego što se pročita. */
const ivicaOk: CSSProperties = { borderColor: "var(--ok)" };
const ivicaUpozorenje: CSSProperties = { borderColor: "var(--warn)" };
